import React, { useState, useContext, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { trackEvent, EVENTS } from '../utils/analytics';
import '../styles/Header.css';

const NAV_ITEMS = [
  { to: '/search', icon: 'fa-search', label: 'Suche' },
  { to: '/portfolio', icon: 'fa-chart-bar', label: 'Portfolio' },
  { to: '/articles', icon: 'fa-newspaper', label: 'Analysen' },
  { to: '/value-investing', icon: 'fa-book', label: 'Value Investing' }
];

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);
  const { token, logout } = useContext(AuthContext);
  const location = useLocation();

  // Close menus when route changes
  useEffect(() => {
    setIsOpen(false);
    setIsMoreMenuOpen(false);
  }, [location.pathname]);

  // Prevent body scroll while the bottom sheet is open
  useEffect(() => {
    document.body.style.overflow = isMoreMenuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMoreMenuOpen]);

  // Close the sheet with Escape
  useEffect(() => {
    if (!isMoreMenuOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setIsMoreMenuOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isMoreMenuOpen]);

  const toggleMenu = () => {
    trackEvent(EVENTS.MENU_TOGGLE, {
      action: !isOpen ? 'open' : 'close',
      screenWidth: window.innerWidth
    });
    setIsOpen(!isOpen);
  };

  const toggleMoreMenu = () => {
    trackEvent(EVENTS.MENU_TOGGLE, {
      action: !isMoreMenuOpen ? 'open' : 'close',
      menu_type: 'more_menu',
      screenWidth: window.innerWidth
    });
    setIsMoreMenuOpen(!isMoreMenuOpen);
  };

  const handleNavigation = (destination, isMobile = false) => {
    trackEvent(isMobile ? EVENTS.MOBILE_NAVIGATION : EVENTS.NAVIGATION, {
      destination,
      from: window.location.pathname
    });
    if (isMobile) {
      setIsOpen(false);
      setIsMoreMenuOpen(false);
    }
  };

  const handleLogout = (isMobile = false) => {
    trackEvent(EVENTS.LOGOUT, {
      device: isMobile ? 'mobile' : 'desktop',
      from: window.location.pathname
    });
    logout();
    if (isMobile) {
      setIsOpen(false);
      setIsMoreMenuOpen(false);
    }
  };

  const isActive = (path) => location.pathname === path;
  const isActivePrefix = (path) => location.pathname === path || location.pathname.startsWith(`${path}/`);
  const isHomeActive = location.pathname === '/';
  const isSearchActive = isActivePrefix('/search') || isActivePrefix('/stock-detail') || isActive('/search-explained');
  const isPortfolioActive = isActivePrefix('/portfolio');
  const isMoreActive = isMoreMenuOpen || ['/articles', '/value-investing', '/login', '/register', '/impressum', '/datenschutz']
    .some((p) => isActivePrefix(p));

  return (
    <>
      {/* Main Header */}
      <header className="header">
        <div className="header-container">
          <div className="header-content">
            <Link
              to="/"
              className="header-logo"
              onClick={() => handleNavigation('home')}
              aria-label="DynaStoxx Startseite"
            >
              <i className="fas fa-chart-line" aria-hidden="true"></i>
              <span>DynaStoxx</span>
            </Link>

            {/* Desktop Navigation */}
            <nav className="header-nav" aria-label="Hauptnavigation">
              {NAV_ITEMS.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  className={`nav-link ${isActivePrefix(item.to) ? 'active' : ''}`}
                  aria-current={isActivePrefix(item.to) ? 'page' : undefined}
                  onClick={() => handleNavigation(item.to.slice(1))}
                >
                  <i className={`fas ${item.icon}`} aria-hidden="true"></i>
                  <span>{item.label}</span>
                </Link>
              ))}
            </nav>

            {/* Desktop Auth Controls */}
            <div className="header-auth">
              {token ? (
                <button onClick={() => handleLogout(false)} className="header-btn header-btn--ghost">
                  <i className="fas fa-sign-out-alt" aria-hidden="true"></i>
                  <span>Abmelden</span>
                </button>
              ) : (
                <div className="auth-buttons">
                  <Link to="/login" className="header-btn header-btn--ghost" onClick={() => handleNavigation('login')}>
                    Anmelden
                  </Link>
                  <Link to="/register" className="header-btn header-btn--primary" onClick={() => handleNavigation('register')}>
                    Registrieren
                  </Link>
                </div>
              )}
            </div>

            {/* Mobile: compact auth entry point on the right */}
            {!token ? (
              <Link
                to="/login"
                className="header-mobile-action mobile-only"
                aria-label="Anmelden"
                onClick={() => handleNavigation('login', true)}
              >
                <i className="fas fa-user" aria-hidden="true"></i>
              </Link>
            ) : (
              <Link
                to="/portfolio"
                className="header-mobile-action header-mobile-action--signed-in mobile-only"
                aria-label="Mein Portfolio"
                onClick={() => handleNavigation('portfolio', true)}
              >
                <i className="fas fa-user-check" aria-hidden="true"></i>
              </Link>
            )}

            {/* Tablet menu button */}
            <button
              onClick={toggleMenu}
              className="mobile-menu-button tablet-only"
              aria-label={isOpen ? 'Menü schließen' : 'Menü öffnen'}
              aria-expanded={isOpen}
            >
              <i className={`fas ${isOpen ? 'fa-times' : 'fa-bars'}`} aria-hidden="true"></i>
            </button>
          </div>

          {/* Tablet dropdown menu */}
          {isOpen && (
            <div className="mobile-menu tablet-only">
              {NAV_ITEMS.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  className="mobile-menu-item"
                  onClick={() => handleNavigation(item.to.slice(1), true)}
                >
                  <i className={`fas ${item.icon}`} aria-hidden="true"></i>
                  <span>{item.label}</span>
                </Link>
              ))}
              {token ? (
                <button onClick={() => handleLogout(true)} className="mobile-menu-item">
                  <i className="fas fa-sign-out-alt" aria-hidden="true"></i>
                  <span>Abmelden</span>
                </button>
              ) : (
                <>
                  <Link to="/login" className="mobile-menu-item" onClick={() => handleNavigation('login', true)}>
                    <i className="fas fa-sign-in-alt" aria-hidden="true"></i>
                    <span>Anmelden</span>
                  </Link>
                  <Link to="/register" className="mobile-menu-item highlight" onClick={() => handleNavigation('register', true)}>
                    <i className="fas fa-user-plus" aria-hidden="true"></i>
                    <span>Registrieren</span>
                  </Link>
                </>
              )}
            </div>
          )}
        </div>
      </header>

      {/* Mobile Bottom Navigation Bar */}
      <nav className="mobile-bottom-nav" aria-label="Mobile Navigation">
        <Link
          to="/"
          className={`bottom-nav-item ${isHomeActive ? 'active' : ''}`}
          aria-current={isHomeActive ? 'page' : undefined}
          onClick={() => handleNavigation('home', true)}
        >
          <i className="fas fa-home" aria-hidden="true"></i>
          <span>Start</span>
        </Link>

        <Link
          to="/search"
          className={`bottom-nav-item ${isSearchActive ? 'active' : ''}`}
          aria-current={isSearchActive ? 'page' : undefined}
          onClick={() => handleNavigation('search', true)}
        >
          <i className="fas fa-search" aria-hidden="true"></i>
          <span>Suche</span>
        </Link>

        <Link
          to="/portfolio"
          className={`bottom-nav-item ${isPortfolioActive ? 'active' : ''}`}
          aria-current={isPortfolioActive ? 'page' : undefined}
          onClick={() => handleNavigation('portfolio', true)}
        >
          <i className="fas fa-chart-bar" aria-hidden="true"></i>
          <span>Portfolio</span>
        </Link>

        <button
          className={`bottom-nav-item ${isMoreActive ? 'active' : ''}`}
          onClick={toggleMoreMenu}
          aria-expanded={isMoreMenuOpen}
          aria-haspopup="dialog"
        >
          <i className={`fas ${isMoreMenuOpen ? 'fa-times' : 'fa-ellipsis-h'}`} aria-hidden="true"></i>
          <span>Mehr</span>
        </button>
      </nav>

      {/* Mobile "More" bottom sheet */}
      <div
        className={`more-menu-overlay ${isMoreMenuOpen ? 'active' : ''}`}
        onClick={() => setIsMoreMenuOpen(false)}
        aria-hidden={!isMoreMenuOpen}
      >
        <div
          className={`more-menu-sheet ${isMoreMenuOpen ? 'active' : ''}`}
          role="dialog"
          aria-modal="true"
          aria-label="Menü"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="more-menu-grabber" aria-hidden="true"></div>
          <div className="more-menu-header">
            <h3>Menü</h3>
            <button className="more-menu-close" onClick={() => setIsMoreMenuOpen(false)} aria-label="Menü schließen">
              <i className="fas fa-times" aria-hidden="true"></i>
            </button>
          </div>

          <div className="more-menu-content">
            <div className="more-menu-section">
              <Link to="/articles" className={`more-menu-item ${isActivePrefix('/articles') ? 'active' : ''}`} onClick={() => handleNavigation('articles', true)}>
                <div className="more-menu-icon"><i className="fas fa-newspaper" aria-hidden="true"></i></div>
                <span>Analysen</span>
                <i className="fas fa-chevron-right more-menu-arrow" aria-hidden="true"></i>
              </Link>
              <Link to="/value-investing" className={`more-menu-item ${isActive('/value-investing') ? 'active' : ''}`} onClick={() => handleNavigation('value-investing', true)}>
                <div className="more-menu-icon"><i className="fas fa-book" aria-hidden="true"></i></div>
                <span>Value Investing</span>
                <i className="fas fa-chevron-right more-menu-arrow" aria-hidden="true"></i>
              </Link>
              <Link to="/search-explained" className={`more-menu-item ${isActive('/search-explained') ? 'active' : ''}`} onClick={() => handleNavigation('search-explained', true)}>
                <div className="more-menu-icon"><i className="fas fa-lightbulb" aria-hidden="true"></i></div>
                <span>Suche erklärt</span>
                <i className="fas fa-chevron-right more-menu-arrow" aria-hidden="true"></i>
              </Link>
            </div>

            <div className="more-menu-divider"></div>

            <div className="more-menu-section">
              {token ? (
                <button className="more-menu-item logout" onClick={() => handleLogout(true)}>
                  <div className="more-menu-icon"><i className="fas fa-sign-out-alt" aria-hidden="true"></i></div>
                  <span>Abmelden</span>
                </button>
              ) : (
                <>
                  <Link to="/login" className="more-menu-item" onClick={() => handleNavigation('login', true)}>
                    <div className="more-menu-icon"><i className="fas fa-sign-in-alt" aria-hidden="true"></i></div>
                    <span>Anmelden</span>
                    <i className="fas fa-chevron-right more-menu-arrow" aria-hidden="true"></i>
                  </Link>
                  <Link to="/register" className="more-menu-item highlight" onClick={() => handleNavigation('register', true)}>
                    <div className="more-menu-icon highlight"><i className="fas fa-user-plus" aria-hidden="true"></i></div>
                    <span>Registrieren</span>
                    <i className="fas fa-chevron-right more-menu-arrow" aria-hidden="true"></i>
                  </Link>
                </>
              )}
            </div>
          </div>

          <div className="more-menu-footer">
            <div className="more-menu-footer-links">
              <Link to="/impressum" onClick={() => handleNavigation('impressum', true)}>Impressum</Link>
              <span aria-hidden="true">·</span>
              <Link to="/datenschutz" onClick={() => handleNavigation('datenschutz', true)}>Datenschutz</Link>
            </div>
            <span>DynaStoxx · Value Investing leicht gemacht</span>
          </div>
        </div>
      </div>
    </>
  );
};

export default Header;
